/**
 * Admin → Automations.
 *
 * Lists automation rules seeded by migration 0002. Each row carries a
 * toggle that PATCHes is_enabled. Disabling here pauses the rule at
 * the cron worker (worker-scheduler reads the flag before firing).
 *
 * Config editing is a v2 concern — for v1 the only knob is on/off.
 */

import { Power } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useAutomations, useUpdateAutomation } from '@/hooks/use-automations.js';

const ruleTypeLabels: Record<string, string> = {
  contract_expiry: 'Contract expiry warnings',
  report_ready: 'Monthly report review tickets',
  bau_burn_rate: 'BAU burn-rate warnings',
  sla_overage: 'SLA overage flags',
  stale_engagement: 'Stale engagement nudges',
};

const ruleTypeDescriptions: Record<string, string> = {
  contract_expiry:
    'Daily cron fires admin tickets at 90/60/30/7 days before contract end.',
  report_ready:
    'Monthly cron fires a review ticket alongside each generated report.',
  bau_burn_rate:
    "Warns when a BAU contract's hours usage outpaces its remaining months.",
  sla_overage:
    'Flags SLA contracts that have crossed their monthly hours allocation.',
  stale_engagement:
    "Nudges engagements that haven't logged time in 14+ days.",
};

export function AdminAutomationsList() {
  const automationsQuery = useAutomations();
  const update = useUpdateAutomation();

  if (automationsQuery.isLoading) {
    return <AdminAutomationsListSkeleton />;
  }
  if (automationsQuery.isError) {
    return (
      <div className="p-8 text-ink-300">Couldn't load automations.</div>
    );
  }
  const automations = automationsQuery.data ?? [];

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850 sticky top-0 z-10">
        <div className="text-[12px] mono text-ink-300 uppercase tracking-[0.06em]">
          Admin
        </div>
        <h1 className="text-[19px] font-semibold text-ink-100 mt-0.5">
          Automations
        </h1>
        <p className="text-[12.5px] text-ink-300 mt-1.5">
          Background rules that fire system tickets. Disable here to pause —
          the cron will read the flag on its next run.
        </p>
      </header>

      <div className="p-6 max-w-3xl">
        {automations.length === 0 ? (
          <div className="rounded-md border border-ink-600 bg-ink-850 p-8 text-center text-ink-300">
            No automations configured yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {automations.map((a) => (
              <li
                key={a.id}
                className="rounded-md border border-ink-600 bg-ink-850 px-4 py-3 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-medium text-ink-100">
                      {ruleTypeLabels[a.type] ?? a.name}
                    </span>
                    <Badge tone={a.isEnabled ? 'green' : 'neutral'}>
                      {a.isEnabled ? 'ON' : 'OFF'}
                    </Badge>
                  </div>
                  <p className="text-[12.5px] text-ink-300 mt-1">
                    {ruleTypeDescriptions[a.type] ?? 'No description.'}
                  </p>
                  {Object.keys(a.config).length > 0 && (
                    <pre className="mt-2 text-[11.5px] mono text-ink-300 bg-ink-900 border border-ink-700 rounded px-2 py-1 overflow-x-auto">
                      {JSON.stringify(a.config, null, 2)}
                    </pre>
                  )}
                </div>
                <button
                  onClick={() =>
                    update.mutate(
                      { id: a.id, input: { isEnabled: !a.isEnabled } },
                      {
                        onSuccess: () =>
                          toast.success(
                            a.isEnabled
                              ? `Disabled "${a.name}"`
                              : `Enabled "${a.name}"`
                          ),
                      }
                    )
                  }
                  disabled={update.isPending}
                  className={
                    'shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] focus-ring border ' +
                    (a.isEnabled
                      ? 'bg-ink-700 border-ink-500 text-ink-100 hover:bg-ink-600'
                      : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')
                  }
                  aria-pressed={a.isEnabled}
                  aria-label={`Toggle ${a.name}`}
                >
                  <Power size={12} />
                  {a.isEnabled ? 'Disable' : 'Enable'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AdminAutomationsListSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-2.5 w-12 mb-1" />
        <Skeleton className="h-5 w-32 mb-1.5" />
        <Skeleton className="h-3 w-2/3 max-w-[420px]" />
      </header>
      <div className="p-6 max-w-3xl">
        <ul className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <li
              key={i}
              className="rounded-md border border-ink-600 bg-ink-850 px-4 py-3 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3.5 w-48" inline />
                  <Skeleton className="h-5 w-10" inline />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
              <Skeleton className="h-8 w-20 shrink-0" inline />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
